import { Title } from '@solidjs/meta'
import { useParams } from '@solidjs/router'
import { type Component, createMemo, createSignal, Show } from 'solid-js'
import Button from '~/components/Button'
import GoBack from '~/components/GoBack'
import Table from '~/components/Table'
import useAlert from '~/components/useAlert'
import { useToast } from '~/components/useToast'
import { useServerTable } from '~/hooks/useServerTable'
import { type Path, useNavigate } from '~/router'
import { type FormFieldType, type FormResponseType, formatAnswer, formService, isAnswerableField, isSignature } from '~/services/form.service'
import { resolveError } from '~/utils/error'

/**
 * Respuestas de un formulario. Las columnas se arman con la definición actual
 * del formulario; una respuesta vieja a una pregunta ya borrada simplemente no
 * tiene columna, pero sigue guardada y sale en el Excel del servidor.
 */
const RespuestasPage: Component = () => {
  const params = useParams()
  const navigate = useNavigate()
  const toast = useToast()
  const alert = useAlert()

  const formId = createMemo(() => Number(params.id))
  const [isExporting, setIsExporting] = createSignal(false)
  /** La definición del formulario llega junto a la primera página de respuestas. */
  const [formMeta, setFormMeta] = createSignal<{ id: number; title: string; fields: FormFieldType[] } | null>(null)

  const table = useServerTable<FormResponseType>({
    fetcher: async params => {
      const result = await formService.responses(formId(), params as Parameters<typeof formService.responses>[1])
      setFormMeta(result.form)
      return { items: result.items, meta: result.meta }
    },
    params: () => ({})
  })

  const fields = createMemo(() => (formMeta()?.fields ?? []).filter(isAnswerableField))

  const columns = createMemo(() => [
    {
      header: 'Recibida',
      sticky: 'left' as const,
      minWidth: 190,
      accessor: (r: FormResponseType) => new Date(r.createdAt).toLocaleString('es-CO')
    },
    ...fields().map(field => ({
      header: field.label || field.id,
      accessor: (r: FormResponseType) => {
        const value = r.answers?.[field.id]

        // Una firma es una imagen: se muestra, no su data URL.
        if (isSignature(value)) {
          return <img src={value} alt="Firma" class="h-10 rounded border border-border bg-surface" />
        }

        const text = formatAnswer(value)
        return (
          <span class="block max-w-xs truncate" title={text}>
            {text}
          </span>
        )
      }
    }))
  ])

  const handleExport = async () => {
    setIsExporting(true)
    try {
      const name = (formMeta()?.title ?? 'respuestas').replace(/[^\w\s-]/g, '').trim() || 'respuestas'
      await formService.exportResponses(formId(), `${name}.xlsx`)
      toast.addToast({ title: 'Excel descargado', type: 'success' })
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    } finally {
      setIsExporting(false)
    }
  }

  const handleDelete = async (response: FormResponseType) => {
    const ok = await alert.confirmDelete({
      title: 'Eliminar respuesta',
      message: 'Esta respuesta se borrará y no saldrá en próximas exportaciones.'
    })
    if (!ok) return

    try {
      await formService.deleteResponse(formId(), response.id)
      toast.addToast({ title: 'Respuesta eliminada', type: 'success' })
      await table.refetch()
    } catch (err: unknown) {
      toast.addToast({ title: resolveError(err), type: 'danger' })
    }
  }

  return (
    <>
      <Title>Respuestas | Florez Jaramillo</Title>

      <div class="flex items-center gap-3 mb-6">
        <GoBack label="Volver a formularios" fallbackPath="/admin/formularios" />
        <div class="min-w-0">
          <h1 class="text-xl font-bold text-foreground truncate">{formMeta()?.title ?? 'Respuestas'}</h1>
          <p class="text-sm text-foreground-subtle">{table.total()} respuesta(s) recibidas</p>
        </div>
      </div>

      <Show when={table.error()}>
        <div class="rounded-xl border border-danger-soft bg-danger-soft/30 px-4 py-3 text-sm text-danger mb-4">{resolveError(table.error())}</div>
      </Show>

      <Table<FormResponseType>
        title="Respuestas"
        subtitle="Lo que ha llegado desde el enlace público"
        headerActions={
          <div class="flex gap-2">
            <Button variant="soft" color="default" onClick={() => navigate(`/admin/formularios/${formId()}` as Path)} prependIcon="material-symbols-light:edit-outline">
              Editar formulario
            </Button>
            <Button onClick={handleExport} disabled={isExporting() || table.data().length === 0} prependIcon="material-symbols-light:table-view-outline">
              {isExporting() ? 'Generando...' : 'Exportar a Excel'}
            </Button>
          </div>
        }
        data={table.data()}
        columns={columns()}
        keyField="id"
        loading={table.loading()}
        serverSide
        total={table.total()}
        page={table.page()}
        perPage={table.perPage()}
        onPageChange={table.onPageChange}
        onPerPageChange={table.onPerPageChange}
        emptyMessage="Todavía no hay respuestas. Comparte el enlace del formulario."
        actions={response => <Button size="sm" variant="soft" color="danger" onClick={() => handleDelete(response)} tooltip="Eliminar" icon="material-symbols-light:delete-outline" />}
      />

      <alert.AlertComponent />
    </>
  )
}

export default RespuestasPage
